import React, {useState} from 'react';
import {Col, Row, Statistic, Typography} from 'antd';
import {DownOutlined, UpOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {PodExemptPointInput, PodExemptReportDetailType} from '../../graphql/podExempt/types';
import PodExemptFenceDrawMap from './PodExemptFenceDrawMap';
import PodExemptFlowTimeline from './PodExemptFlowTimeline';
import PodExemptMaterialGallery from './PodExemptMaterialGallery';
import PodExemptOpinionCards, {hasOpinionCards} from './PodExemptOpinionCards';

/** pointsJson 为提交时原样落库的 [{latitude, longitude}]，解析失败按空处理（地图只是回显辅助） */
export function parseFencePoints(pointsJson: string | null): PodExemptPointInput[] {
  if (!pointsJson) {
    return [];
  }
  try {
    const parsed = JSON.parse(pointsJson);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (p: unknown): p is PodExemptPointInput =>
        typeof (p as PodExemptPointInput)?.latitude === 'number' &&
        typeof (p as PodExemptPointInput)?.longitude === 'number',
    );
  } catch {
    return [];
  }
}

interface Props {
  detail: PodExemptReportDetailType;
  /** 附加内容（如 500m 参考地图），渲染在时间线之前 */
  extra?: React.ReactNode;
  /** 审核端：展示数据参考面板（单量/客诉/识别率等，监控侧接入前显示待接入） */
  showDataPanel?: boolean;
  /** 审核页时间线在右栏自渲染，置 false 关闭正文内时间线 */
  showTimeline?: boolean;
  dictNameOf?: (code: string) => string;
}

/** 分区卡（与审核页 Section 同款：白底圆角卡 + 加粗小标题），抽屉正文与审核页视觉一致 */
const Section: React.FC<{title: React.ReactNode; children: React.ReactNode}> = ({title, children}) => (
  <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-5 py-4">
    <div className="text-sm font-semibold mb-3.5">{title}</div>
    {children}
  </div>
);

/** 字段行（与审核页 InfoField 同款：左灰标签 + 右值 + 底部细分割线） */
const InfoField: React.FC<{label: React.ReactNode; value?: React.ReactNode; span?: 1 | 2}> = ({
  label,
  value,
  span = 1,
}) => (
  <div
    className={`flex gap-3 items-baseline py-2 border-b border-gray-100 min-w-0 ${
      span === 2 ? 'col-span-2' : ''
    }`}
  >
    <span className="text-gray-400 text-sm flex-none w-24">{label}</span>
    <span className="break-words min-w-0 flex-1">{value ?? '-'}</span>
  </div>
);

/** 上报详情正文（抽屉用：与审核页同款白卡分区风格；审核页自身有更全的工作台布局）。 */
const PodExemptDetailContent: React.FC<Props> = ({
  detail,
  extra,
  showDataPanel = false,
  showTimeline = true,
  dictNameOf,
}) => {
  const {t} = useTranslation();
  // 围栏地图默认收起：展开才挂载 PodExemptFenceDrawMap（顺带省掉未展开时的地图初始化与边界查询）
  const [fenceOpen, setFenceOpen] = useState(false);
  const nameOf = dictNameOf ?? ((code: string) => code);
  const {report, address, fence} = detail;

  return (
    <div className="flex flex-col gap-4">
      <Section title={t('podExempt.detail.basicSection')}>
        <div className="grid grid-cols-2 gap-x-8">
          <InfoField
            label={t('podExempt.field.scopeType')}
            value={report.scopeType === 'FENCE' ? t('podExempt.scope.fence') : t('podExempt.scope.single')}
          />
          <InfoField
            label={t('podExempt.field.addressType')}
            value={report.addressType ? nameOf(report.addressType) : '-'}
          />
          {/* 与列表同口径显示仓名（后端详情链路回填），缺失回退编码 */}
          <InfoField
            label={t('podExempt.field.warehouseCode')}
            value={report.warehouseName ?? report.warehouseCode}
          />
          <InfoField label={t('podExempt.field.zipcode')} value={report.zipcode} />
          <InfoField
            label={t('podExempt.field.reasons')}
            value={report.reasonCodes.map(nameOf).join(', ') || '-'}
            span={2}
          />
          <InfoField label={t('podExempt.field.reasonRemark')} value={report.reasonRemark} span={2} />
          {/* 驳回原因 / 驳回意见不在基础信息里渲染：统一由下方 PodExemptOpinionCards 出卡
              （与审核页同一位置、同一数据源，避免同一条驳回重复出现） */}
        </div>
      </Section>

      {showDataPanel && (
        <Section title={t('podExempt.review.dataPanel')}>
          <Row gutter={12}>
            {(['hit30d', 'complaint90d', 'plateRate', 'driverSample'] as const).map((key) => (
              <Col span={6} key={key}>
                <Statistic title={t(`podExempt.review.${key}`)} value="-" valueStyle={{fontSize: 18}} />
              </Col>
            ))}
          </Row>
          <Typography.Text type="secondary" style={{fontSize: 12}}>
            {t('podExempt.review.dataPending')}
          </Typography.Text>
        </Section>
      )}

      {/* 审核意见卡：排在地址 / 围栏信息之前——被驳回时这是最先要看的信息 */}
      {hasOpinionCards(detail.flows) && <PodExemptOpinionCards flows={detail.flows} dictNameOf={nameOf} />}

      {address && (
        <Section title={t('podExempt.detail.addressSection')}>
          <div className="grid grid-cols-2 gap-x-8">
            <InfoField label={t('podExempt.field.rawAddress')} value={address.rawAddress} span={2} />
            <InfoField label={t('podExempt.field.waybillNo')} value={report.trackingNumber} />
            <InfoField
              label={t('podExempt.field.coordinates')}
              value={
                address.latitude != null && address.longitude != null
                  ? `${address.latitude}, ${address.longitude}`
                  : '-'
              }
            />
          </div>
        </Section>
      )}

      {fence && (
        <Section title={t('podExempt.detail.fenceSection')}>
          <div className="grid grid-cols-2 gap-x-8 mb-3">
            <InfoField label={t('podExempt.field.fenceName')} value={fence.name} />
            {/* 地址数只对生效围栏物化（与列表同口径）：非生效显示 -，生效未回填显示统计中 */}
            <InfoField
              label={t('podExempt.field.addressCount')}
              value={
                fence.addressCount ??
                (report.status === 'EFFECTIVE' ? t('podExempt.detail.counting') : '-')
              }
            />
          </div>
          {/* 折叠条（与审核页同款）：地图会吞滚轮事件，铺开时页面滚到此处就被地图接走缩放，故默认收起 */}
          <div className="border border-gray-100 rounded-md px-3.5 py-2.5 flex items-center justify-between gap-3">
            <span className="text-gray-400 text-sm min-w-0 truncate">
              {t('podExempt.review.fenceBarNote')}
            </span>
            <Typography.Link className="flex-none" onClick={() => setFenceOpen((open) => !open)}>
              {t(fenceOpen ? 'podExempt.review.collapseFence' : 'podExempt.review.viewFence')}{' '}
              {fenceOpen ? <UpOutlined /> : <DownOutlined />}
            </Typography.Link>
          </div>
          {fenceOpen && (
            <div className="mt-3">
              <PodExemptFenceDrawMap
                zipcode={fence.zipcode}
                points={parseFencePoints(fence.pointsJson)}
                onPointsChange={() => undefined}
                readonly
              />
            </div>
          )}
        </Section>
      )}

      <Section title={t('podExempt.detail.materials')}>
        <PodExemptMaterialGallery materials={detail.materials} reasonCodes={detail.report.reasonCodes} />
      </Section>

      {extra}

      {showTimeline && (
        <Section title={t('podExempt.detail.timeline')}>
          <PodExemptFlowTimeline flows={detail.flows} dictNameOf={nameOf} />
        </Section>
      )}
    </div>
  );
};

export default PodExemptDetailContent;
